/** 流水线布局 · ECharts 主题（品牌主色 + 深浅色） */
import { mixHex, hexToRgbTriplet } from '../../../../../config/themePalette';
import { getStatisticsChartPrimary } from '../../../common/lib/statisticsChartPalette';

const rgbaFromHex = (hex, alpha) => {
	const rgb = hexToRgbTriplet(hex);
	if (!rgb) return `rgba(44, 123, 229, ${alpha})`;
	return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`;
};

const buildAxisTheme = (primary, isLight) => ({
	axisLine: { lineStyle: { color: rgbaFromHex(primary, isLight ? 0.22 : 0.26), width: 1 } },
	axisTick: { show: false },
	/* 夜间轴标签用接近 labelColor 的亮度，近黑画布上 #94a3b8 偏糊 */
	axisLabel: { color: isLight ? '#748194' : '#b8c5d9', fontSize: 10 },
	splitLine: { lineStyle: { color: rgbaFromHex(primary, isLight ? 0.1 : 0.08), type: 'dashed' } },
});

const buildTooltipTheme = (primary, isLight) => ({
	backgroundColor: isLight ? 'rgba(255, 255, 255, 0.96)' : 'rgba(15, 23, 42, 0.94)',
	borderColor: rgbaFromHex(primary, isLight ? 0.3 : 0.45),
	borderWidth: 1,
	textStyle: { color: isLight ? '#344050' : '#e8edf5', fontSize: 11 },
	extraCssText: isLight ? `border-radius: 8px; box-shadow: 0 4px 18px rgba(15, 23, 42, 0.1);` : `border-radius: 8px; box-shadow: 0 0 24px ${rgbaFromHex(primary, 0.22)};`,
});

const buildBaseChartTheme = (isLight = false) => {
	const primary = getStatisticsChartPrimary(isLight);
	const accentLight = mixHex(primary, '#ffffff', isLight ? 0.22 : 0.28);
	const axis = buildAxisTheme(primary, isLight);
	const tooltip = buildTooltipTheme(primary, isLight);
	const labelColor = isLight ? '#748194' : '#b8c5d9';
	const axisPointerShadow = rgbaFromHex(primary, isLight ? 0.12 : 0.15);
	const mutedBar = isLight ? mixHex(primary, '#ffffff', 0.78) : 'rgba(71, 85, 105, 0.55)';
	const pieBorderColor = isLight ? 'rgba(255, 255, 255, 0.95)' : 'rgba(15, 23, 42, 0.92)';

	return {
		axis,
		tooltip,
		labelColor,
		axisPointerShadow,
		mutedBar,
		pieBorderColor,
		pieItemShadow: rgbaFromHex(primary, isLight ? 0.28 : 0.38),
		brandPrimary: primary,
		brandAccentLight: accentLight,
		createBarStyle: (color, selectedPhase, phase) => ({
			color: selectedPhase && selectedPhase !== phase ? mutedBar : color,
			opacity: selectedPhase && selectedPhase !== phase ? 0.35 : 1,
			borderRadius: [8, 8, 0, 0],
			shadowBlur: selectedPhase === phase ? 12 : 0,
			shadowColor: rgbaFromHex(primary, isLight ? 0.3 : 0.4),
		}),
		barEmphasis: {
			itemStyle: {
				shadowBlur: 16,
				shadowColor: rgbaFromHex(accentLight, isLight ? 0.35 : 0.5),
			},
		},
	};
};

export const getPrimaryPhaseChartTheme = (isLight = false) => {
	const theme = buildBaseChartTheme(isLight);
	return {
		...theme,
		createBarStyle: (color, selectedPhase, phase) => ({
			...theme.createBarStyle(color, selectedPhase, phase),
			borderRadius: [6, 6, 0, 0],
		}),
	};
};

/** 环形图 · 标签显示模式 */
export const DONUT_PIE_LABEL_MODE = {
	ALL: 'all',
	PICK: 'pick',
};

const normalizePickedKeys = (pickedKeys) => {
	if (pickedKeys instanceof Set) return pickedKeys;
	if (Array.isArray(pickedKeys)) return new Set(pickedKeys);
	return new Set();
};

/** 环形图 · 全部常显或自选多选标签；selectedKey 用透明度强调，不外移扇区 */
export const mapDonutPieDataWithLabel = (
	items,
	{
		chartTheme,
		getKey = (item) => item.phase ?? item.name,
		labelFormatter,
		labelMode = DONUT_PIE_LABEL_MODE.ALL,
		pickedKeys = new Set(),
		selectedKey = null,
	},
) => {
	const pickedSet = normalizePickedKeys(pickedKeys);
	const isPickMode = labelMode === DONUT_PIE_LABEL_MODE.PICK;
	const hasPicked = isPickMode && pickedSet.size > 0;
	const hasSliceSelection = selectedKey != null && selectedKey !== '';
	const dimOthers = hasSliceSelection || hasPicked;

	return items.map((item) => {
		const key = getKey(item);
		const showLabel = isPickMode ? pickedSet.has(key) : true;
		const labelEmphasized = isPickMode && pickedSet.has(key);
		const sliceSelected = hasSliceSelection ? key === selectedKey : false;
		const emphasized = hasSliceSelection ? sliceSelected : labelEmphasized;

		return {
			...item,
			itemStyle: {
				...(item.itemStyle ?? {}),
				opacity: dimOthers && !emphasized ? 0.38 : 1,
				shadowBlur: hasSliceSelection && sliceSelected ? 10 : 0,
				shadowColor: hasSliceSelection && sliceSelected ? chartTheme.pieItemShadow : 'transparent',
			},
			label: {
				show: showLabel,
				color: chartTheme.labelColor,
				fontSize: 10,
				lineHeight: 13,
				formatter: labelFormatter,
			},
			labelLine: {
				show: showLabel,
				length: 10,
				length2: 8,
				smooth: 0.2,
				lineStyle: { color: item.itemStyle?.color ?? chartTheme.labelColor, width: 1 },
			},
		};
	});
};

export const resolveFullPieRadius = (needsLabelRoom, compact = false) => {
	if (compact) return needsLabelRoom ? '46%' : '58%';
	return needsLabelRoom ? '50%' : '68%';
};

export const resolveDonutPieRadius = (needsLabelRoom, compact = false, { largeCenter = false } = {}) => {
	if (largeCenter) {
		if (compact) {
			return needsLabelRoom ? ['46%', '54%'] : ['50%', '66%'];
		}
		return needsLabelRoom ? ['48%', '58%'] : ['54%', '72%'];
	}
	if (compact) {
		return needsLabelRoom ? ['38%', '54%'] : ['44%', '66%'];
	}
	return needsLabelRoom ? ['40%', '58%'] : ['48%', '72%'];
};

const formatPieRadiusPct = (ratio) => `${Math.round(ratio * 1000) / 10}%`;

const toPieRadiusPct = (pct) => `${Math.round(pct * 10) / 10}%`;

/** 中心圆孔内径：按中心指标行数留足显示空间（兼顾环宽可见） */
export const resolveMetricPieCenterHoleInnerPct = (centerMetricCount = 1, { needsLabelRoom = false, compact = false } = {}) => {
	const count = Math.min(Math.max(centerMetricCount, 1), 3);
	if (count === 3) {
		if (compact) return needsLabelRoom ? 50 : 52;
		return needsLabelRoom ? 52 : 54;
	}
	if (count === 2) {
		if (compact) return needsLabelRoom ? 48 : 52;
		return needsLabelRoom ? 50 : 54;
	}
	if (compact) return needsLabelRoom ? 44 : 48;
	return needsLabelRoom ? 46 : 50;
};

/** 从中心圆孔向外排同心环 */
export const buildMetricPieRadiiFromCenterHole = ({ ringCount = 1, centerMetricCount = 1, needsLabelRoom = false, compact = false } = {}) => {
	const holeInner = resolveMetricPieCenterHoleInnerPct(centerMetricCount, { needsLabelRoom, compact });
	const gap = compact ? 3 : 4;
	const ringWidth = compact ? 7 : 8;
	const singleRingSpan = compact ? 12 : 14;

	if (ringCount <= 1) {
		return [[toPieRadiusPct(holeInner), toPieRadiusPct(holeInner + singleRingSpan)]];
	}

	const radii = [];
	let inner = holeInner;
	for (let index = 0; index < ringCount; index += 1) {
		const outer = inner + ringWidth;
		radii.push([toPieRadiusPct(inner), toPieRadiusPct(outer)]);
		inner = outer + gap;
	}
	return radii;
};

export const resolveMultiMetricPieRadii = ({ metricCount = 1, isDonut = true, needsLabelRoom = false, compact = false, centerHoleRatio } = {}) => {
	if (metricCount <= 1) {
		const radius = isDonut ? resolveDonutPieRadius(needsLabelRoom, compact) : resolveFullPieRadius(needsLabelRoom, compact);
		return [radius];
	}

	const singleDonut = resolveDonutPieRadius(needsLabelRoom, compact);
	const singlePie = resolveFullPieRadius(needsLabelRoom, compact);
	const parsePct = (value) => parseFloat(String(value).replace('%', '')) / 100;

	const maxOuter = isDonut ? parsePct(singleDonut[1]) : parsePct(singlePie);
	const defaultCenterHole = compact ? 0.28 : 0.32;
	const centerHole = centerHoleRatio ?? (isDonut ? defaultCenterHole : 0);
	const gap = compact ? 0.024 : 0.032;
	const available = maxOuter - centerHole;
	const ringWidth = Math.max((available - gap * (metricCount - 1)) / metricCount, compact ? 0.06 : 0.08);

	const radii = [];
	for (let index = 0; index < metricCount; index += 1) {
		const inner = centerHole + index * (ringWidth + gap);
		const outer = Math.min(inner + ringWidth, maxOuter);
		radii.push([formatPieRadiusPct(inner), formatPieRadiusPct(outer)]);
	}
	return radii;
};

/** 组成图 · 多指标环形/饼图半径（实心饼多指标也走同心环，标签挂最外环） */
export const resolveLayeredMetricPieRadii = ({
	metricCount = 1,
	centerMetricCount = 1,
	isDonut = true,
	needsLabelRoom = false,
	compact = false,
	largeCenter = false,
	expandedSingleRing = false,
} = {}) => {
	if (!isDonut) {
		if (metricCount > 1) {
			return resolveMultiMetricPieRadii({ metricCount, isDonut: false, needsLabelRoom, compact });
		}
		return [resolveFullPieRadius(needsLabelRoom, compact)];
	}
	if (metricCount > 1 || expandedSingleRing || centerMetricCount > 1) {
		return buildMetricPieRadiiFromCenterHole({
			ringCount: Math.max(metricCount, 1),
			centerMetricCount,
			needsLabelRoom,
			compact,
		});
	}
	const radius = resolveDonutPieRadius(needsLabelRoom, compact, { largeCenter });
	return [radius];
};
/** 经营看板 · 主阶段双环占比（内圈单数 / 外圈合同金额） */
export const resolveDualPhasePieRadii = ({ needsLabelRoom = false, compact = false } = {}) => {
	if (compact) {
		return needsLabelRoom
			? [
					['46%', '52%'],
					['55%', '64%'],
				]
			: [
					['48%', '56%'],
					['59%', '72%'],
				];
	}
	return needsLabelRoom
		? [
				['52%', '58%'],
				['62%', '71%'],
			]
		: [
				['54%', '62%'],
				['64%', '76%'],
			];
};

/** 二元占比图（如完成状态）小扇区最小角度，避免占比极小时不可见 */
export const PIE_BINARY_SLICE_MIN_ANGLE = 12;

export const PIE_MULTI_RING_BINARY_SLICE_MIN_ANGLE = 10;

export const PIE_BINARY_SLICE_PAD_ANGLE = 1;

/** 客户分布图 · 品牌色与底色（与流水线其它图表一致） */
export const buildChinaMapStyle = (isLight = false) => {
	const primary = getStatisticsChartPrimary(isLight);
	const accentLight = mixHex(primary, '#ffffff', isLight ? 0.22 : 0.28);
	const baseArea = isLight ? '#f4f7fb' : 'rgba(15, 18, 42, 0.72)';
	const gradientAnchor = isLight ? '#ffffff' : '#0b1024';

	return {
		baseArea,
		border: rgbaFromHex(primary, isLight ? 0.22 : 0.32),
		/** visualMap 连续色带：低值贴近底色，高值饱和主色，避免全省同色 */
		provinceGradient: isLight
			? [mixHex(primary, gradientAnchor, 0.9), mixHex(primary, gradientAnchor, 0.68), mixHex(primary, gradientAnchor, 0.38), mixHex(primary, '#000000', 0.06), primary]
			: [mixHex(primary, gradientAnchor, 0.94), mixHex(primary, gradientAnchor, 0.72), mixHex(primary, gradientAnchor, 0.42), mixHex(primary, '#ffffff', 0.1), primary],
		point: accentLight,
		emphasisArea: rgbaFromHex(primary, isLight ? 0.14 : 0.4),
		emphasisBorder: accentLight,
		highlightBorder: rgbaFromHex(accentLight, isLight ? 0.95 : 1),
		pointShadow: rgbaFromHex(primary, isLight ? 0.35 : 0.48),
	};
};

/** visualMap 映射区间：订单量相同时按排名拉开色阶（含筛选高亮但无订单的省份） */
export const buildChinaMapVisualRange = (provinceRows = []) => {
	if (!provinceRows.length) {
		return { min: 1, max: 2, useRank: false };
	}

	const counts = provinceRows.map((row) => row.count);
	const minCount = Math.min(...counts);
	const maxCount = Math.max(...counts);

	if (maxCount === 0 && provinceRows.length > 1) {
		return { min: 1, max: provinceRows.length, useRank: true };
	}

	if (maxCount === 0) {
		return { min: 1, max: 2, useRank: false };
	}

	if (minCount === maxCount && provinceRows.length > 1) {
		return { min: 1, max: provinceRows.length, useRank: true };
	}

	return {
		min: Math.max(1, minCount),
		max: Math.max(minCount + 1, maxCount),
		useRank: false,
	};
};
